import * as THREE from "three";
import { B } from "../shared/world";
import { ITEMS } from "../shared/items";

// Every texture is painted procedurally at 16x16, imitating the classic
// (pre-2019) Pocket Edition look — clustered grain, bevels, dark outlines —
// without shipping Mojang's files.

export const T = {
  GRASS_TOP: 0, GRASS_SIDE: 1, DIRT: 2, STONE: 3, SAND: 4, WATER: 5, LOG_SIDE: 6, LOG_TOP: 7,
  LEAVES: 8, PLANKS: 9, COBBLE: 10, BEDROCK: 11, STONEBRICK: 12, GOLD: 13, GLASS: 14,
  CHEST_FRONT: 15, CHEST_SIDE: 16, CHEST_TOP: 17,
  ASPHALT: 18, BRICK: 19, BOOKSHELF: 20, CONCRETE: 21,
  WOOL_WHITE: 22, WOOL_RED: 23, WOOL_BLUE: 24, WOOL_YELLOW: 25, WOOL_BLACK: 26,
} as const;
const LAST_TILE = T.WOOL_BLACK;
const WOOL: Record<number, string> = {
  [T.WOOL_WHITE]: "#e4e7e7", [T.WOOL_RED]: "#a52b26", [T.WOOL_BLUE]: "#363b9e", [T.WOOL_YELLOW]: "#f0c22c", [T.WOOL_BLACK]: "#222226",
};
export const ATLAS_COLS = 8;
export const ATLAS_ROWS = 4;

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

type RGB = [number, number, number];
const hex = (h: string): RGB => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
const shade = (c: RGB, f: number): RGB => [Math.min(255, c[0] * f), Math.min(255, c[1] * f), Math.min(255, c[2] * f)];

class Tile {
  data = new Uint8ClampedArray(16 * 16 * 4);
  set(x: number, y: number, c: RGB, a = 255) {
    x = ((x % 16) + 16) % 16;
    y = ((y % 16) + 16) % 16;
    const i = (y * 16 + x) * 4;
    this.data[i] = c[0];
    this.data[i + 1] = c[1];
    this.data[i + 2] = c[2];
    this.data[i + 3] = a;
  }
  alpha(x: number, y: number) {
    return this.data[(y * 16 + x) * 4 + 3]!;
  }
}

/**
 * Tileable value field with a little clumping, so pixels group into the
 * 1–3px blobs the classic textures have instead of pure static.
 */
function field(r: () => number, clump = 0.5): number[] {
  const raw = Array.from({ length: 256 }, () => r());
  const out: number[] = [];
  for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
    const at = (dx: number, dy: number) => raw[((y + dy + 16) % 16) * 16 + ((x + dx + 16) % 16)]!;
    const n = (at(1, 0) + at(-1, 0) + at(0, 1) + at(0, -1)) / 4;
    out.push(raw[y * 16 + x]! * (1 - clump) + n * clump);
  }
  // Re-normalise to 0..1 so palettes get used evenly.
  const lo = Math.min(...out), hi = Math.max(...out);
  return out.map((v) => (v - lo) / (hi - lo || 1));
}

/** Map a 0..1 value onto a palette ordered dark → light. */
function pal(p: string[], v: number): RGB {
  return hex(p[Math.min(p.length - 1, Math.floor(v * p.length))]!);
}

const GRASS = ["#4c8a26", "#589a2e", "#63a735", "#6db23b", "#79bd44", "#86c94e"];
const DIRT = ["#5b3e29", "#6c4b33", "#7a563b", "#866043", "#94694a", "#a77852"];
const STONE = ["#686868", "#727272", "#7b7b7b", "#838383", "#8c8c8c", "#979797"];
const SAND = ["#cdc28c", "#d5cb97", "#dbd3a0", "#e1d9a9", "#e7e0b6"];
const LOG = ["#3f2f1c", "#4e3a23", "#5c452a", "#6a5032", "#77593a"];
const LEAF = ["#1f5c13", "#2a6e19", "#357f1f", "#418f26", "#4ea12e"];
const PLANK = ["#8f7044", "#9c7c4c", "#a98754", "#b4915c", "#bf9b64"];
const GOLD = ["#e2b52a", "#ebc535", "#f3d243", "#fadf55"];

function paint(i: number): Tile {
  const t = new Tile();
  const r = rng(i * 7919 + 13);
  switch (i) {
    case T.GRASS_TOP: {
      const f = field(r, 0.45);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(GRASS, f[p]!));
      break;
    }
    case T.DIRT: {
      const f = field(r, 0.4);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(DIRT, f[p]!));
      // The light tan pebbles classic dirt is known for.
      for (let k = 0; k < 7; k++) t.set(Math.floor(r() * 16), Math.floor(r() * 16), hex("#b48a64"));
      break;
    }
    case T.GRASS_SIDE: {
      t.data.set(paint(T.DIRT).data);
      const g = field(r, 0.4);
      for (let x = 0; x < 16; x++) {
        // Solid top band, then ragged drips hanging into the dirt.
        let depth = 3 + (r() < 0.55 ? 1 : 0);
        if (r() < 0.3) depth++;
        if (r() < 0.12) depth += 2;
        for (let y = 0; y < depth; y++) t.set(x, y, pal(GRASS, g[y * 16 + x]! * 0.85 + (y === depth - 1 ? 0 : 0.1)));
      }
      break;
    }
    case T.STONE: {
      // Stretch the noise sideways — classic stone has short horizontal streaks.
      const f = field(r, 0.3);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const v = (f[y * 16 + x]! * 2 + f[y * 16 + ((x + 1) % 16)]! + f[y * 16 + ((x + 15) % 16)]!) / 4;
        t.set(x, y, pal(STONE, v));
      }
      break;
    }
    case T.SAND: {
      const f = field(r, 0.2);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(SAND, f[p]!));
      break;
    }
    case T.WATER: {
      const f = field(r, 0.6);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const v = f[y * 16 + x]!;
        t.set(x, y, v > 0.82 ? hex("#5577ee") : v > 0.5 ? hex("#3a5ddc") : hex("#2f4fcf"), 175);
      }
      break;
    }
    case T.LOG_SIDE: {
      // Vertical bark ridges: each column keeps its tone, broken into runs.
      for (let x = 0; x < 16; x++) {
        let base = x % 3 === 0 ? 0.15 : 0.45 + r() * 0.45;
        for (let y = 0; y < 16; y++) {
          if (r() < 0.18) base = Math.max(0, Math.min(0.99, base + (r() - 0.5) * 0.5));
          t.set(x, y, pal(LOG, base));
        }
      }
      break;
    }
    case T.LOG_TOP:
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
        let c: RGB;
        if (d > 6.5) c = pal(LOG, 0.2 + r() * 0.6);
        else if (d < 1.5) c = hex("#957347");
        else c = Math.floor(d) % 2 ? hex("#b8955c") : hex("#a3824e");
        t.set(x, y, shade(c, 0.95 + r() * 0.08));
      }
      break;
    case T.LEAVES: {
      const f = field(r, 0.35);
      const holes = field(r, 0.55);
      for (let p = 0; p < 256; p++) {
        if (holes[p]! > 0.78) t.set(p % 16, p >> 4, [0, 0, 0], 0);
        else t.set(p % 16, p >> 4, pal(LEAF, f[p]!));
      }
      break;
    }
    case T.PLANKS: {
      // Four boards, each with its own tone, a dark bottom seam and a staggered end joint.
      const joints = [3, 11, 7, 14];
      for (let b = 0; b < 4; b++) {
        const tone = [0.55, 0.7, 0.45, 0.62][b]!;
        for (let y = b * 4; y < b * 4 + 4; y++) for (let x = 0; x < 16; x++) {
          let c: RGB;
          if (y % 4 === 3) c = hex("#6e5332");
          else if (x === joints[b]) c = hex("#7a5d38");
          else {
            const grain = r() < 0.12 ? -0.25 : (r() - 0.5) * 0.2;
            c = pal(PLANK, Math.max(0, Math.min(0.99, tone + grain)));
          }
          t.set(x, y, c);
        }
      }
      break;
    }
    case T.COBBLE: {
      // Voronoi stones with an outline, lit from the top-left.
      const pts = Array.from({ length: 10 }, () => ({ x: r() * 16, y: r() * 16, v: 0.25 + r() * 0.6 }));
      const near = (x: number, y: number) => {
        let a = 99, b = 99, idx = 0;
        pts.forEach((p, k) => {
          const dx = Math.min(Math.abs(x - p.x), 16 - Math.abs(x - p.x));
          const dy = Math.min(Math.abs(y - p.y), 16 - Math.abs(y - p.y));
          const d = Math.hypot(dx, dy);
          if (d < a) { b = a; a = d; idx = k; } else if (d < b) b = d;
        });
        return { idx, edge: b - a };
      };
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const n = near(x + 0.5, y + 0.5);
        if (n.edge < 1) {
          t.set(x, y, hex(r() < 0.5 ? "#3f3f3f" : "#4d4d4d"));
          continue;
        }
        const up = near(x + 0.5, y - 0.5), left = near(x - 0.5, y + 0.5);
        const dn = near(x + 0.5, y + 1.5), right = near(x + 1.5, y + 0.5);
        let v = pts[n.idx]!.v + (r() - 0.5) * 0.15;
        if (up.edge < 1 || left.edge < 1) v += 0.3;
        else if (dn.edge < 1 || right.edge < 1) v -= 0.25;
        t.set(x, y, pal(["#5a5a5a", "#6a6a6a", "#7a7a7a", "#8a8a8a", "#9a9a9a", "#acacac"], Math.max(0, Math.min(0.99, v))));
      }
      break;
    }
    case T.BEDROCK: {
      const f = field(r, 0.55);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(["#1c1c1c", "#2e2e2e", "#3f3f3f", "#575757", "#7a7a7a"], f[p]!));
      break;
    }
    case T.STONEBRICK: {
      // Top half: one long brick. Bottom half: two bricks, joint in the middle.
      const f = field(r, 0.3);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const top = y < 8;
        const ly = y % 8;
        const bx = top ? x : (x + 8) % 16;
        const lx = top ? x : bx % 8;
        const mortar = ly === 7 || (!top && bx % 8 === 7) || (top && x === 15);
        let c: RGB;
        if (mortar) c = hex("#4f4f4f");
        else if (ly === 0 || lx === 0) c = hex("#9a9a9a");
        else if (ly === 6 || (!top ? lx === 6 : x === 14)) c = hex("#686868");
        else c = pal(["#717171", "#787878", "#7e7e7e", "#858585"], f[y * 16 + x]!);
        t.set(x, y, c);
      }
      break;
    }
    case T.GOLD: {
      const f = field(r, 0.3);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        let c: RGB;
        if (y === 0 || x === 0) c = hex("#fffbb5");
        else if (y === 15 || x === 15) c = hex("#c8901a");
        else if (y === 1 || x === 1) c = hex("#fdf07a");
        else if (y === 14 || x === 14) c = hex("#dba428");
        else c = pal(GOLD, f[y * 16 + x]!);
        t.set(x, y, c);
      }
      // A couple of shine streaks.
      for (const [x, y] of [[4, 3], [5, 4], [6, 5], [10, 9], [11, 10], [3, 11]]) t.set(x!, y!, hex("#fffbd0"));
      break;
    }
    case T.GLASS:
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const edge = x === 0 || y === 0 || x === 15 || y === 15;
        const corner = (x === 0 || x === 15) && (y === 0 || y === 15);
        if (edge) t.set(x, y, corner ? hex("#9fc4cc") : hex("#dbeef2"));
        else t.set(x, y, [0, 0, 0], 0);
      }
      for (const [x, y] of [[3, 2], [2, 3], [4, 2], [2, 4], [5, 3], [3, 5], [9, 7], [8, 8], [7, 9]]) t.set(x!, y!, hex("#ffffff"), 210);
      break;
    case T.ASPHALT: {
      const f = field(r, 0.25);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(["#2c2c2f", "#333336", "#39393c", "#404043", "#48484b"], f[p]!));
      // Grit catching the light.
      for (let k = 0; k < 9; k++) t.set(Math.floor(r() * 16), Math.floor(r() * 16), hex("#5e5e60"));
      break;
    }
    case T.BRICK: {
      // Classic brick: 4px courses, bricks offset every other course, pale mortar.
      const f = field(r, 0.3);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const course = y >> 2;
        const bx = (x + (course % 2) * 4) % 8;
        let c: RGB;
        if (y % 4 === 3 || bx === 7) c = hex("#a49c94");
        else if (y % 4 === 0) c = hex("#b0634c");
        else c = pal(["#82382a", "#8e4030", "#994836", "#a3503d"], f[y * 16 + x]!);
        t.set(x, y, c);
      }
      break;
    }
    case T.BOOKSHELF: {
      // Plank boards top, middle and bottom with two shelves of coloured spines.
      t.data.set(paint(T.PLANKS).data);
      const spines = ["#7a2420", "#2e3f86", "#2f6a2a", "#6b4a22", "#5a2a6e", "#8a7a2a"];
      for (const y0 of [2, 9]) {
        let x = 1;
        while (x < 15) {
          const wdt = 1 + Math.floor(r() * 2);
          const col = hex(spines[Math.floor(r() * spines.length)]!);
          const hgt = 4 + Math.floor(r() * 2);
          for (let dx = 0; dx < wdt && x + dx < 15; dx++) {
            for (let y = y0; y < y0 + 5; y++) {
              const book = y >= y0 + 5 - hgt;
              t.set(x + dx, y, book ? shade(col, dx === 0 ? 1.15 : 0.9) : hex("#3a2a18"));
            }
          }
          x += wdt;
        }
      }
      break;
    }
    case T.CONCRETE: {
      const f = field(r, 0.5);
      for (let p = 0; p < 256; p++) t.set(p % 16, p >> 4, pal(["#9a9a97", "#a1a19e", "#a7a7a4", "#adadaa"], f[p]!));
      break;
    }
    case T.WOOL_WHITE:
    case T.WOOL_RED:
    case T.WOOL_BLUE:
    case T.WOOL_YELLOW:
    case T.WOOL_BLACK: {
      // Soft clumpy noise with a faint diagonal weave.
      const base = hex(WOOL[i]!);
      const f = field(r, 0.55);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const weave = (x + y) % 4 === 0 ? 0.93 : 1;
        t.set(x, y, shade(base, (0.86 + f[y * 16 + x]! * 0.2) * weave));
      }
      break;
    }
    case T.CHEST_SIDE:
    case T.CHEST_FRONT:
    case T.CHEST_TOP: {
      const f = field(r, 0.35);
      for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
        const edge = x === 0 || y === 0 || x === 15 || y === 15;
        const seam = i !== T.CHEST_TOP && (y === 5 || y === 6);
        let c: RGB;
        if (edge) c = hex("#2b1b0c");
        else if (seam) c = y === 5 ? hex("#3b2712") : hex("#6b4a1e");
        else if (x === 1 || y === 1) c = hex("#c08a3c");
        else c = pal(["#865c22", "#93672a", "#a07230", "#ad7d37"], f[y * 16 + x]!);
        t.set(x, y, c);
      }
      break;
    }
  }
  return t;
}

let atlasCanvas: HTMLCanvasElement | null = null;
export function atlas(): HTMLCanvasElement {
  if (atlasCanvas) return atlasCanvas;
  const c = document.createElement("canvas");
  c.width = ATLAS_COLS * 16;
  c.height = ATLAS_ROWS * 16;
  const g = c.getContext("2d")!;
  for (let i = 0; i <= LAST_TILE; i++) {
    const tile = paint(i);
    g.putImageData(new ImageData(tile.data, 16, 16), (i % ATLAS_COLS) * 16, Math.floor(i / ATLAS_COLS) * 16);
  }
  atlasCanvas = c;
  return c;
}

export function pixelTexture(src: HTMLCanvasElement): THREE.Texture {
  const t = new THREE.CanvasTexture(src);
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestFilter;
  t.generateMipmaps = false;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function tileCanvas(i: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  c.getContext("2d")!.drawImage(atlas(), (i % ATLAS_COLS) * 16, Math.floor(i / ATLAS_COLS) * 16, 16, 16, 0, 0, 16, 16);
  return c;
}

/** Tile index for a block face. dir: 0 +x,1 -x,2 +y,3 -y,4 +z,5 -z */
export function blockTile(b: number, dir: number): number {
  switch (b) {
    case B.GRASS: return dir === 2 ? T.GRASS_TOP : dir === 3 ? T.DIRT : T.GRASS_SIDE;
    case B.DIRT: return T.DIRT;
    case B.STONE: return T.STONE;
    case B.SAND: return T.SAND;
    case B.WATER: return T.WATER;
    case B.LOG: return dir === 2 || dir === 3 ? T.LOG_TOP : T.LOG_SIDE;
    case B.LEAVES: return T.LEAVES;
    case B.PLANKS: return T.PLANKS;
    case B.COBBLE: return T.COBBLE;
    case B.BEDROCK: return T.BEDROCK;
    case B.STONEBRICK: return T.STONEBRICK;
    case B.GOLD: return T.GOLD;
    case B.GLASS: return T.GLASS;
    case B.ASPHALT: return T.ASPHALT;
    case B.BRICK: return T.BRICK;
    case B.BOOKSHELF: return dir === 2 || dir === 3 ? T.PLANKS : T.BOOKSHELF;
    case B.CONCRETE: return T.CONCRETE;
    case B.WOOL_WHITE: return T.WOOL_WHITE;
    case B.WOOL_RED: return T.WOOL_RED;
    case B.WOOL_BLUE: return T.WOOL_BLUE;
    case B.WOOL_YELLOW: return T.WOOL_YELLOW;
    case B.WOOL_BLACK: return T.WOOL_BLACK;
  }
  return T.STONE;
}

// ---------- item sprites ----------
// Drawn as palette-keyed pixel maps, then outlined in near-black like the
// classic item sprites. k = outline, m/M/D = material mid/light/dark,
// h/H = wood handle light/dark, others are per-sprite.

const SWORD = [
  "..............kk",
  ".............kMk",
  "............kMmk",
  "...........kMmDk",
  "..........kMmDk.",
  ".........kMmDk..",
  "........kMmDk...",
  ".......kMmDk....",
  "..kk..kMmDk.....",
  "..kDkkMmDk......",
  "...kDMmDk.......",
  "...kkDDk........",
  "..khkkDDk.......",
  ".khHk.kkDk......",
  "kHHk....kk......",
  "kkk.............",
];

const ARMOR: string[][] = [
  [
    "................",
    "................",
    "................",
    "...kkkkkkkkkk...",
    "..kMMMMMMMMMmk..",
    "..kMmmmmmmmmDk..",
    "..kMmkkkkkkmDk..",
    "..kMDk....kmDk..",
    "..kmDk....kDDk..",
    "..kkkk....kkkk..",
  ],
  [
    "..kkkk....kkkk..",
    ".kMMmkk..kkmmDk.",
    ".kMmmmkkkkmmmDk.",
    ".kMmmmmmmmmmmDk.",
    ".kkMmmmmmmmmDkk.",
    "..kkMmmmmmmDkk..",
    "...kMmmmmmmDk...",
    "...kMmmmmmmDk...",
    "...kMmmmmmmDk...",
    "...kMmmmmmmDk...",
    "...kMmmmmmmDk...",
    "...kkkkkkkkkk...",
  ],
  [
    "...kkkkkkkkkk...",
    "...kMMMMMMMMk...",
    "...kMmmmmmmDk...",
    "...kMmDkkMmDk...",
    "...kMmDkkMmDk...",
    "...kMmDkkMmDk...",
    "...kMmDkkMmDk...",
    "...kMmDkkMmDk...",
    "...kMDDkkMDDk...",
    "...kkkkkkkkkk...",
  ],
  [
    "................",
    "................",
    "...kkkk..kkkk...",
    "...kMmk..kMmk...",
    "...kMmk..kMmk...",
    "..kkMmk..kMmkk..",
    ".kMmmDk..kMmmDk.",
    ".kDDDDk..kDDDDk.",
    ".kkkkkk..kkkkkk.",
  ],
];

const FOOD: Record<string, { rows: string[]; extra: Record<string, string> }> = {
  apple: {
    rows: [
      "................",
      ".......kk.......",
      "......khk.kk....",
      ".......khkgk....",
      "....kkkkhkkk....",
      "...kMMmmkmmmk...",
      "..kMMmmmmmmmDk..",
      "..kMmmmmmmmmDk..",
      "..kmmmmmmmmmDk..",
      "..kmmmmmmmmDDk..",
      "...kmmmmmmmDk...",
      "...kDmmmmmDDk...",
      "....kDDkkDDk....",
      ".....kk..kk.....",
    ],
    extra: { g: "#4caf2a" },
  },
  bread: {
    rows: [
      "................",
      "................",
      "................",
      "......kkkkkk....",
      "....kkMMmMMmkk..",
      "...kMMmMMmMMmDk.",
      "..kMmMMmMMmmmDk.",
      ".kMmmmmmmmmmDDk.",
      ".kmmmmmmmmmDDk..",
      ".kDmmmmmmDDDk...",
      "..kDDDDDDDkk....",
      "...kkkkkkk......",
    ],
    extra: {},
  },
  steak: {
    rows: [
      "................",
      "................",
      ".....kkkkk......",
      "....kMMmmmkk....",
      "...kMmmmmmmmk...",
      "..kMmmmmmmmmmk..",
      "..kmmmmmmmmmmDk.",
      "..kmmmmmmmmwwDk.",
      "..kDmmmmmmwkwDk.",
      "...kDmmmmmmwwk..",
      "....kDDmmmmDk...",
      ".....kkDDDDk....",
      ".......kkkk.....",
    ],
    extra: { w: "#efe7d4" },
  },
  sign: {
    rows: [
      "................",
      "kkkkkkkkkkkkkkkk",
      "kMMMMMMMMMMMMMMk",
      "kMmmmmmmmmmmmmDk",
      "kMmDDDDDDDDmmmDk",
      "kMmmmmmmmmmmmmDk",
      "kMmDDDDDDDmmmmDk",
      "kMmmmmmmmmmmmmDk",
      "kDDDDDDDDDDDDDDk",
      "kkkkkkkhHkkkkkkk",
      ".......hHk......",
      ".......hHk......",
      ".......hHk......",
      ".......hHk......",
      ".......kkk......",
    ],
    extra: {},
  },
};
FOOD.golden_apple = FOOD.apple!;

// Iron rim around a pale face; the needle is drawn on top at whatever angle it points.
const COMPASS = [
  ".....kkkkkk.....",
  "...kkMMMMMMkk...",
  "..kkMMMMMMMMkk..",
  ".kkMMffffffMmkk.",
  ".kMMffffffffmDk.",
  "kMMffffffffffDDk",
  "kMMffffffffffDDk",
  "kMMffffffffffDDk",
  "kMMffffffffffDDk",
  "kMMffffffffffDDk",
  "kMMffffffffffDDk",
  ".kMMffffffffDDk.",
  ".kkmmffffffDDkk.",
  "..kkDDDDDDDDkk..",
  "...kkDDDDDDkk...",
  ".....kkkkkk.....",
];
/** Like the real compass sprite: 32 frames of needle. */
const COMPASS_FRAMES = 32;

function drawCompass(g: CanvasRenderingContext2D, frame: number) {
  drawRows(g, COMPASS, ITEMS.compass!.color, 0, { f: "#e2dccb" });
  const a = (frame / COMPASS_FRAMES) * Math.PI * 2;
  const dot = (t: number, color: string) => {
    g.fillStyle = color;
    g.fillRect(Math.floor(7.5 + Math.sin(a) * t + 0.5), Math.floor(7.5 - Math.cos(a) * t + 0.5), 1, 1);
  };
  for (let t = 0; t <= 2.6; t += 0.5) dot(-t, "#6e6e6e");
  for (let t = 0; t <= 4.2; t += 0.5) dot(t, "#d61f1f");
  dot(0, "#3a3a3a");
}

// Ranged gear. s = string, a/A = arrowhead light/dark, f/F = fletching light/dark.
// The bow's three "pulling" frames are drawn while the string is held back, like Minecraft's.
const BOW_EXTRA = { s: "#e9e9e9", a: "#d4d4d4", A: "#8c8c8c", f: "#f4f4f4", F: "#bdbdbd" };
const SPRITES: Record<string, { rows: string[]; extra: Record<string, string> }> = {
  bow: {
    rows: [
      "................",
      ".kkkk...........",
      "kMMMMkkk........",
      "kDDDDMMMk.......",
      ".kksDDDMMk......",
      "....skkDHHk.....",
      ".....s.kHHHk....",
      "......s.kHDmk...",
      ".......s.kDmmk..",
      "........s.kDmk..",
      ".........skDmk..",
      "..........sDDmk.",
      "...........sDmk.",
      "...........kDmk.",
      "...........kDmk.",
      "............kk..",
    ],
    extra: BOW_EXTRA,
  },
  bow_pulling_0: {
    rows: [
      "................",
      ".kkkk...........",
      "kMMMMkkk........",
      "kDDDDMMMk..kk...",
      ".kssDDDMMkkak...",
      "...skkkDHHhAk...",
      "....s..kHhHk....",
      "....ss.khHDmk...",
      ".....skhkkDmmk..",
      "....kfsk..kDmk..",
      "....kffss.kDmk..",
      ".....kk.sskDDmk.",
      "..........ssDmk.",
      "...........sDmk.",
      "...........kDmk.",
      "............kk..",
    ],
    extra: BOW_EXTRA,
  },
  bow_pulling_1: {
    rows: [
      "................",
      ".kkkk.......k...",
      "kMMMMkkk...kak..",
      "kDDDDMMMk.kaAk..",
      ".kskDDDMMkhAk...",
      "...skkkDHhHk....",
      "...s...kHhHk....",
      "....s..khHDmk...",
      "....s.khkkDmmk..",
      "....kshk..kDmk..",
      "...kfss...kDmk..",
      "...kffkss.kDDmk.",
      "....kk...sskDmk.",
      "...........sDmk.",
      "...........kDmk.",
      "............kk..",
    ],
    extra: BOW_EXTRA,
  },
  bow_pulling_2: {
    rows: [
      "............k...",
      ".kkkk......kak..",
      "kMMMMkkk..kaAk..",
      "kDDDDMMMk.khk...",
      ".kskDDDMMkhk....",
      "..sskkkDHhHk....",
      "...s...khHHk....",
      "...s..khkHDmk...",
      "...ss.khkkDmmk..",
      "....skhk..kDmk..",
      "...kfhk...kDmk..",
      "...kffss..kDDmk.",
      "....kk.sssskDmk.",
      "..........ssDmk.",
      "...........kDmk.",
      "............kk..",
    ],
    extra: BOW_EXTRA,
  },
  arrow: {
    rows: [
      "................",
      "............kk..",
      "..........kkaMk.",
      ".........kDaaak.",
      ".........khhak..",
      "........khhkDk..",
      ".......khhk.k...",
      "......khhk......",
      ".....khhk.......",
      "....khhk........",
      "...khhk.........",
      ".kkfhk..........",
      "kFfhfk..........",
      "kFffk...........",
      ".kFFk...........",
      "..kk............",
    ],
    extra: { a: "#d8d8d8", f: "#f4f4f4", F: "#bdbdbd" },
  },
  // Line hanging off the tip with the hook at the bottom; once cast, the line is out in the world instead.
  fishing_rod: {
    rows: [
      "................",
      ".............k..",
      "............khs.",
      "...........khhs.",
      "..........khhks.",
      ".........khhk.s.",
      "........khhk..s.",
      ".......khhk...s.",
      "......khhk....s.",
      ".....khhk.....s.",
      "....khhk......s.",
      "...khhk.......s.",
      "..khHk.....k.ka.",
      ".khHk.......kaak",
      ".kHk.........kk.",
      "..k.............",
    ],
    extra: { s: "#d6d6d6", a: "#a9a9a9" },
  },
  fishing_rod_cast: {
    rows: [
      "................",
      ".............k..",
      "............khk.",
      "...........khhk.",
      "..........khhk..",
      ".........khhk...",
      "........khhk....",
      ".......khhk.....",
      "......khhk......",
      ".....khhk.......",
      "....khhk........",
      "...khhk.........",
      "..khHk..........",
      ".khHk...........",
      ".kHk............",
      "..k.............",
    ],
    extra: {},
  },
};
/** Where the line leaves a rod, in item-sprite units (centre of the tip pixel). */
export const ROD_TIP = [13.5 / 16 - 0.5, 0.5 - 2.5 / 16] as const;

/** Item a sprite variant belongs to: "bow_pulling_2" → "bow". */
export function spriteBase(id: string): string {
  return id.replace(/_(pulling_\d|cast)$/, "");
}

/** Hotbar icon for a compass whose needle points `angle` radians clockwise from up. */
export function compassIcon(angle: number): string {
  const frame = ((Math.round((angle / (Math.PI * 2)) * COMPASS_FRAMES) % COMPASS_FRAMES) + COMPASS_FRAMES) % COMPASS_FRAMES;
  const key = `compass:${frame}`;
  let url = iconUrlCache.get(key);
  if (!url) {
    const c = document.createElement("canvas");
    c.width = c.height = 16;
    drawCompass(c.getContext("2d")!, frame);
    url = c.toDataURL();
    iconUrlCache.set(key, url);
  }
  return url;
}

function drawRows(g: CanvasRenderingContext2D, rows: string[], color: string, top: number, extra: Record<string, string> = {}) {
  const m = hex(color);
  const palette: Record<string, RGB> = {
    m, M: shade(m, 1.28), D: shade(m, 0.68), h: hex("#8a6433"), H: hex("#5a3e1c"), k: hex("#191919"),
    ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, hex(v)])),
  };
  rows.forEach((row, y) => [...row].forEach((ch, x) => {
    const c = palette[ch];
    if (!c) return;
    g.fillStyle = `rgb(${c.map((v) => v | 0).join(",")})`;
    g.fillRect(x, y + top, 1, 1);
  }));
}

const iconCanvasCache = new Map<string, HTMLCanvasElement>();
const iconUrlCache = new Map<string, string>();

export function itemCanvas(id: string): HTMLCanvasElement {
  const cached = iconCanvasCache.get(id);
  if (cached) return cached;
  const def = ITEMS[spriteBase(id)];
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  const g = c.getContext("2d")!;
  const sprite = SPRITES[id];
  if (sprite && def) drawRows(g, sprite.rows, def.color, 0, sprite.extra);
  else if (def?.kind === "sword") drawRows(g, SWORD, def.color, 0);
  else if (def?.kind === "armor") {
    const rows = ARMOR[def.slot!]!;
    drawRows(g, rows, def.color, Math.floor((16 - rows.length) / 2));
  } else if (def?.kind === "compass") drawCompass(g, 0);
  else if (def) {
    const f = FOOD[id] ?? FOOD.apple!;
    drawRows(g, f.rows, id === "sign" ? "#b8945f" : def.color, Math.floor((16 - f.rows.length) / 2), f.extra);
  }
  iconCanvasCache.set(id, c);
  return c;
}

export function itemIcon(id: string): string {
  let url = iconUrlCache.get(id);
  if (!url) {
    url = itemCanvas(id).toDataURL();
    iconUrlCache.set(id, url);
  }
  return url;
}

// ---------- HUD icons (9x9 like the classic icons sheet) ----------

const HEART = [
  ".kk...kk.",
  "kRrk.kRrk",
  "kRWrkrrrk",
  "krrrrrrrk",
  "krrrrrrDk",
  ".krrrrDk.",
  "..krrDk..",
  "...kDk...",
  "....k....",
];
const CHESTPLATE = [
  ".kkk.kkk.",
  "kWwwkwwgk",
  "kwwwwwwgk",
  ".kwwwwgk.",
  ".kwwwwgk.",
  ".kwwwwgk.",
  ".kwwwwgk.",
  ".kggggk..",
  "..kkkk...",
];

function hudIcon(key: string, rows: string[], colors: Record<string, string>, halfFrom = -1, halfColor = "") {
  const cached = iconUrlCache.get(key);
  if (cached) return cached;
  const url = hudIconCanvas(key, rows, colors, halfFrom, halfColor).toDataURL();
  iconUrlCache.set(key, url);
  return url;
}

function hudIconCanvas(key: string, rows: string[], colors: Record<string, string>, halfFrom = -1, halfColor = "") {
  const cached = iconCanvasCache.get(key);
  if (cached) return cached;
  const c = document.createElement("canvas");
  c.width = c.height = 9;
  const g = c.getContext("2d")!;
  rows.forEach((row, y) => [...row].forEach((ch, x) => {
    let col = colors[ch];
    if (!col) return;
    if (halfFrom >= 0 && x >= halfFrom && ch !== "k") col = halfColor;
    g.fillStyle = col;
    g.fillRect(x, y, 1, 1);
  }));
  iconCanvasCache.set(key, c);
  return c;
}

export function heartIcon(kind: "full" | "half" | "empty"): string {
  const full = { k: "#000000", r: "#e01414", R: "#ff6a6a", W: "#ffffff", D: "#a10b0b" };
  const empty = { k: "#000000", r: "#3a0a0a", R: "#3a0a0a", W: "#3a0a0a", D: "#3a0a0a" };
  if (kind === "empty") return hudIcon("heart:e", HEART, empty);
  if (kind === "full") return hudIcon("heart:f", HEART, full);
  return hudIcon("heart:h", HEART, full, 5, "#3a0a0a");
}

/** Same 9x9 heart as a canvas, for drawing into nametags. */
export function heartCanvas(kind: "full" | "half" | "empty"): HTMLCanvasElement {
  const full = { k: "#000000", r: "#e01414", R: "#ff6a6a", W: "#ffffff", D: "#a10b0b" };
  const empty = { k: "#000000", r: "#3a0a0a", R: "#3a0a0a", W: "#3a0a0a", D: "#3a0a0a" };
  if (kind === "empty") return hudIconCanvas("heart:e", HEART, empty);
  if (kind === "full") return hudIconCanvas("heart:f", HEART, full);
  return hudIconCanvas("heart:h", HEART, full, 5, "#3a0a0a");
}

export function armorIcon(kind: "full" | "half" | "empty"): string {
  const full = { k: "#1c1c1c", w: "#d8d8d8", W: "#ffffff", g: "#9a9a9a" };
  const empty = { k: "#1c1c1c", w: "#3c3c3c", W: "#3c3c3c", g: "#3c3c3c" };
  if (kind === "empty") return hudIcon("armor:e", CHESTPLATE, empty);
  if (kind === "full") return hudIcon("armor:f", CHESTPLATE, full);
  return hudIcon("armor:h", CHESTPLATE, full, 5, "#3c3c3c");
}

export function dirtBackground(): string {
  const c = document.createElement("canvas");
  c.width = c.height = 16;
  const g = c.getContext("2d")!;
  const t = paint(T.DIRT);
  for (let i = 0; i < t.data.length; i += 4) {
    t.data[i] = t.data[i]! * 0.25;
    t.data[i + 1] = t.data[i + 1]! * 0.25;
    t.data[i + 2] = t.data[i + 2]! * 0.25;
  }
  g.putImageData(new ImageData(t.data, 16, 16), 0, 0);
  return c.toDataURL();
}

export function planksBackground(): string {
  return tileCanvas(T.PLANKS).toDataURL();
}

export function stoneBackground(): string {
  return tileCanvas(T.STONE).toDataURL();
}
